import { Camera, FileText, FolderOpen, UploadCloud, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { optimizeImageFile } from './ImageOptimization';

export function CanonicalFileDropzone({
  label,
  file,
  onFileChange,
  onError,
  accept = 'application/pdf,.pdf',
  maxSizeBytes = 20 * 1024 * 1024,
  disabled = false,
  helpText = 'PDF, máximo 20 MB.',
  allowCamera = false,
  cameraFileName = 'comprobante',
}: {
  label: string;
  file: File | null;
  onFileChange: (file: File | null) => void;
  onError?: (message: string) => void;
  accept?: string;
  maxSizeBytes?: number;
  disabled?: boolean;
  helpText?: string;
  allowCamera?: boolean;
  cameraFileName?: string;
}) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [dragging, setDragging] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');

  const select = async (candidate: File | null) => {
    if (!candidate) return;
    const acceptedTypes = accept.split(',').map((item) => item.trim());
    const extension = '.' + (candidate.name.split('.').pop() ?? '').toLowerCase();
    const accepted = acceptedTypes.some(
      (item) => item === candidate.type || item.toLowerCase() === extension,
    );
    if (!accepted) {
      onError?.('El archivo no tiene un formato permitido.');
      return;
    }
    if (candidate.size > maxSizeBytes) {
      onError?.('El documento debe pesar máximo 20 MB.');
      return;
    }
    onError?.('');
    if (!candidate.type.startsWith('image/') || typeof createImageBitmap !== 'function') {
      onFileChange(candidate);
      return;
    }
    onFileChange(await optimizeImageFile(candidate));
  };

  const drop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (!disabled) void select(event.dataTransfer.files[0] ?? null);
  };

  const openPicker = () => !disabled && inputRef.current?.click();

  const releaseCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const closeCamera = () => {
    releaseCamera();
    setCameraOpen(false);
  };

  const openCamera = async () => {
    if (disabled) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      const message = 'Este navegador no permite abrir la cámara. Usa Elegir archivo.';
      setCameraError(message);
      onError?.(message);
      return;
    }
    try {
      setCameraError('');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOpen(true);
    } catch {
      const message = 'No se pudo abrir la cámara. Revisa el permiso del navegador.';
      setCameraError(message);
      onError?.(message);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          onError?.('No se pudo capturar la fotografía. Inténtalo nuevamente.');
          return;
        }
        closeCamera();
        void select(
          new File([blob], `${cameraFileName.replace(/\.[^.]+$/, '')}.jpg`, { type: 'image/jpeg' }),
        );
      },
      'image/jpeg',
      0.92,
    );
  };

  useEffect(() => {
    if (cameraOpen && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      void videoRef.current.play();
    }
  }, [cameraOpen]);

  useEffect(() => () => releaseCamera(), []);
  const keyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPicker();
    }
  };

  return (
    <section className="ui-file-dropzone">
      <span className="ui-file-dropzone__label" id={inputId + '-label'}>
        {label}
      </span>
      <div
        className="ui-file-dropzone__target"
        data-dragging={dragging || undefined}
        data-disabled={disabled || undefined}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={openPicker}
        onKeyDown={keyDown}
        onDragEnter={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
        }}
        onDrop={drop}
        aria-label="Seleccionar archivo o arrastrarlo"
      >
        <input
          ref={inputRef}
          id={inputId}
          className="ui-file-dropzone__input"
          type="file"
          accept={accept}
          disabled={disabled}
          aria-label={label}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            void select(event.target.files?.[0] ?? null);
            event.target.value = '';
          }}
        />
        <UploadCloud aria-hidden="true" />
        <strong>
          {allowCamera
            ? 'Selecciona, arrastra o toma una foto'
            : 'Selecciona o arrastra un archivo'}
        </strong>
        <span>{helpText}</span>
      </div>
      <div className="ui-file-dropzone__actions">
        <button type="button" disabled={disabled} onClick={openPicker}>
          <FolderOpen aria-hidden="true" />
          Elegir archivo
        </button>
        {allowCamera ? (
          <button type="button" disabled={disabled} onClick={() => void openCamera()}>
            <Camera aria-hidden="true" />
            Tomar foto
          </button>
        ) : null}
      </div>
      {cameraError ? <p className="ui-file-dropzone__camera-error">{cameraError}</p> : null}
      {cameraOpen ? (
        <div className="ui-file-dropzone__camera-backdrop" role="presentation">
          <section
            className="ui-file-dropzone__camera-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Tomar fotografía del comprobante"
          >
            <header>
              <strong>Tomar fotografía</strong>
              <button type="button" onClick={closeCamera} aria-label="Cerrar cámara">
                <X aria-hidden="true" />
              </button>
            </header>
            <video ref={videoRef} autoPlay muted playsInline />
            <canvas ref={canvasRef} hidden />
            <footer>
              <button type="button" onClick={closeCamera}>
                Cancelar
              </button>
              <button type="button" onClick={capturePhoto}>
                <Camera aria-hidden="true" /> Capturar foto
              </button>
            </footer>
          </section>
        </div>
      ) : null}
      {file ? (
        <div className="ui-file-dropzone__file" aria-live="polite">
          <FileText aria-hidden="true" />
          <span>
            <strong>{file.name}</strong>
            <small>{(file.size / 1024 / 1024).toFixed(2)} MB</small>
          </span>
          <button
            type="button"
            onClick={() => onFileChange(null)}
            aria-label={`Quitar ${file.name}`}
            title="Quitar archivo"
          >
            <X />
          </button>
        </div>
      ) : null}
    </section>
  );
}
